import { useState } from 'react'
import { Locate, LocateFixed, LocateOff } from 'lucide-react'

interface MyLocationButtonProps {
  /** Called with the resolved [lon, lat] on a successful geolocation. */
  onLocated: (coords: [number, number]) => void
  /**
   * Tailwind utility classes for absolute positioning. The button is a
   * standalone floating pill on the map; the parent decides where it
   * lives. Default places it top-5 with a left offset that clears the
   * CoordInput card on standard widths.
   */
  positionClass?: string
}

/**
 * Standalone "use my location" button. Sits as its own floating pill
 * alongside (not inside) the coordinate search card on both the homepage
 * and the Explore page. Three states: idle / locating / error. Graceful
 * no-op if `navigator.geolocation` is missing or denied.
 *
 * No positioning of its own — the parent decides where this lives
 * (typically inside a `flex` cluster next to <CoordInput />).
 */
export function MyLocationButton({
  onLocated,
  // No default position. The previous default (absolute left-[380px])
  // was off-screen on mobile viewports < 380 px wide, hiding the
  // button entirely. Parents now place this in a positioned cluster
  // (typically top-right next to BasemapToggle) so it's reachable at
  // every viewport.
  positionClass = '',
}: MyLocationButtonProps) {
  const [status, setStatus] = useState<'idle' | 'locating' | 'error'>('idle')

  function locate() {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('error')
      return
    }
    setStatus('locating')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStatus('idle')
        onLocated([pos.coords.longitude, pos.coords.latitude])
      },
      () => setStatus('error'),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    )
  }

  const Icon = status === 'locating' ? LocateFixed : status === 'error' ? LocateOff : Locate

  return (
    <button
      type="button"
      onClick={locate}
      aria-label="Use my location"
      title={
        status === 'error'
          ? 'Could not get your location — check browser permissions.'
          : 'Use my location'
      }
      className={[
        positionClass,
        'inline-flex items-center justify-center shrink-0 w-11 h-11 cursor-pointer',
        'rounded-[var(--radius-lg)] transition-colors',
        'bg-[var(--color-elev-strong)] backdrop-blur-[14px]',
        'border border-[var(--color-border)]',
        'shadow-[var(--shadow-coord)]',
        status === 'error'
          ? 'text-[var(--color-signal)]'
          : 'text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/60',
      ].join(' ')}
    >
      <Icon size={16} aria-hidden className={status === 'locating' ? 'animate-pulse' : ''} />
    </button>
  )
}
